import React, { useContext } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';

import { AuthProvider, AuthContext } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';
import HelpPage from './pages/HelpPage/HelpPage';
import ProtectedLayout from './components/ProtectedLayout/ProtectedLayout';
import './App.css';

// ADMIN
import SessionsAndMessages from './pages/Admin/SessionsAndMessages/SessionsAndMessages';
import Unanswered from './pages/Admin/Unanswered/Unanswered';
import Bookings from './pages/Admin/Bookings/Bookings';
import Users from './pages/Admin/Users/Users';
import Hotels from './pages/Admin/Hotels/Hotels';
import Groups from './pages/Admin/Groups/Groups';
import Forms from './pages/Admin/Forms/Forms';
import ApiKeys from './pages/Admin/ApiKeys/ApiKeys';
import Restaurants from './pages/Admin/Restaurants/Restaurants';
import EventRooms from './pages/Admin/Events/Events';
import Modules from './pages/Admin/Modules/Modules';
import ShowLogs from './pages/Admin/ShowLogs/ShowLogs';
import SyncContent from './pages/Admin/SyncContent/SyncContent';
import Licensing from './pages/Admin/Licensing/Licensing';
import ConciergeAssistant from './pages/Admin/ConciergeAssistant/ConciergeAssistant';

// CLIENTS
import MyHotels from './pages/Clients/MyHotels/MyHotels';
import MyRestaurants from './pages/Clients/MyRestaurants/MyRestaurants';
import MyEventRooms from './pages/Clients/MyEventRooms/MyEventRooms';
import MySettings from './pages/Clients/MySettings/MySettings';
import MySessionsAndMessages from './pages/Clients/MySessionsAndMessages/MySessionsAndMessages';
import MyMessages from './pages/Clients/MyMessages/MyMessages';
import MyChatMessages from './pages/Clients/MyChatMessages/MyChatMessages';
import MyChatTopics from './pages/Clients/MyChatTopics/MyChatTopics';
import MyBookings from './pages/Clients/MyBookings/MyBookings';
import MyUnansweredMessages from './pages/Clients/MyChatUnanswered/MyChatUnanswered';
import MyDashboard from './pages/Clients/MyDashboard/MyDashboard';
import MyHandoff from './pages/Clients/MyHandoff/MyHandoff';
import HandoffConfig from './pages/Admin/HandoffConfig/HandoffConfig';
import CsatConfig from './pages/Admin/CsatConfig/CsatConfig';
import RevenueConfig from './pages/Admin/RevenueConfig/RevenueConfig';
import Dashboard from './pages/Admin/Dashboard/Dashboard';

// página para testes
import PageTest from './pages/TestPage/TestPage';
import Settings from './pages/Admin/Settings/Settings';

// wrapper genérico
function RequireAuth({ allowedRoles = [] }) {
  const { token, currentUser } = useContext(AuthContext);
  if (!token) return <Navigate to="/login" replace />;
  if (!currentUser) return null; // ainda a carregar

  // se foram definidos roles permitidos, verifica se a role do utilizador está lá
  if (allowedRoles.length && !allowedRoles.includes(currentUser.role)) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function RoleBasedRedirect() {
  const { token, currentUser } = useContext(AuthContext);
  if (!token) return <Navigate to="/login" replace />;
  if (!currentUser) return null; // ainda a carregar

  const isSuper = currentUser.role === 'superadmin';
  const isOperator = currentUser.role === 'operator';
  //const isGroup = currentUser.role === "group_admin";
  //const isHotel = currentUser.role === "hotel_admin";
  const initialPath = isSuper
    ? '/dashboard'
    : isOperator
    ? '/my-atendimento'
    : '/my-dashboard';

  return <Navigate to={initialPath} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* ROTAS PÚBLICAS */}
          <Route path="login" element={<LoginPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />

          {/* PROTEGIDAS */}
          <Route element={<ProtectedLayout />}>
            {/* EXIGE TOKEN */}
            <Route element={<RequireAuth />}>
              <Route index element={<RoleBasedRedirect />} />

              {/* ROTAS SUPERADMIN */}
              <Route element={<RequireAuth allowedRoles={['superadmin']} />}>
                {/* <Route path="sessions" element={<Sessions />} />
                <Route
                  path="sessions/:sessionId"
                  element={<SessionsDetails />}
                /> */}
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="sessions" element={<SessionsAndMessages />} />
                <Route path="unanswered" element={<Unanswered />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="test-page" element={<PageTest />} />
                <Route path="groups" element={<Groups />} />
                <Route path="hotels" element={<Hotels />} />
                <Route path="restaurants" element={<Restaurants />} />
                <Route path="events" element={<EventRooms />} />
                <Route path="modules" element={<Modules />} />
                <Route path="sync-content" element={<SyncContent />} />
                <Route path="licensing" element={<Licensing />} />
                <Route path="concierge-assistant" element={<ConciergeAssistant />} />
                <Route path="settings" element={<Settings />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="users" element={<Users />} />
                <Route path="forms" element={<Forms />} />
                <Route path="show-logs" element={<ShowLogs />} />
              </Route>

              {/* ROTAS GROUPADMIN */}
              <Route
                element={
                  <RequireAuth allowedRoles={['group_admin', 'hotel_admin']} />
                }
              >
                {/* <Route path="my-sessions" element={<MySessions />} />
                <Route
                  path="my-sessions/:sessionId"
                  element={<MySessionsDetails />}
                /> */}
                <Route path="my-dashboard" element={<MyDashboard />} />
                <Route path="my-sessions" element={<MySessionsAndMessages />} />
                <Route path="my-messages" element={<MyMessages />} />
                <Route
                  path="my-unanswered-messages"
                  element={<MyUnansweredMessages />}
                />
                <Route path="my-hotels" element={<MyHotels />} />
                <Route path="my-restaurants" element={<MyRestaurants />} />
                <Route path="my-bookings" element={<MyBookings />} />
                <Route path="my-events" element={<MyEventRooms />} />
                <Route path="my-chat-topics" element={<MyChatTopics />} />
                <Route path="my-chat-messages" element={<MyChatMessages />} />
                <Route path="my-settings" element={<MySettings />} />
              </Route>

              {/* CONFIGURAÇÃO DO ATENDIMENTO HUMANO — gestão de definições/departamentos/
                  operadores; superadmin gere qualquer hotel, group_admin/hotel_admin só
                  os seus (mesmo guard de posse usado no resto da app). */}
              <Route
                element={
                  <RequireAuth allowedRoles={['superadmin', 'group_admin', 'hotel_admin']} />
                }
              >
                <Route path="handoff-config" element={<HandoffConfig />} />
                <Route path="csat-config" element={<CsatConfig />} />
                <Route path="revenue-config" element={<RevenueConfig />} />
              </Route>

              {/* ROTAS OPERADOR (Atendimento Humano) — isolada do bloco acima:
                  um operador não-admin não deve ter acesso a Hotéis/Restaurantes/
                  Definições, só à consola. hotel_admin/group_admin também entram
                  aqui se também atenderem conversas. */}
              <Route
                element={
                  <RequireAuth
                    allowedRoles={['operator', 'group_admin', 'hotel_admin']}
                  />
                }
              >
                <Route path="my-atendimento" element={<MyHandoff />} />
              </Route>

              {/* 404 INTERNO */}
              <Route path="*" element={<RoleBasedRedirect />} />
              <Route path="help" element={<HelpPage />} />
            </Route>
          </Route>
          {/* 404 PÚBLICO */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
